<template>
  <div
    class="-mx-2 my-[-0.3125rem] flex items-center px-2 py-[0.3125rem]"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <input
      type="checkbox"
      :aria-label="label"
      :checked="isSelected"
      :disabled="!isSelectable"
      class="rounded border border-gray-300 bg-white text-blue-500 transition-colors focus:ring-0 focus:ring-offset-0 focus-visible:outline focus-visible:outline-blue-500 disabled:bg-gray-100 dark:border-border dark:bg-sidebar dark:checked:border-muted-foreground dark:checked:bg-muted-foreground dark:disabled:bg-sidebar"
      @input="onInputChange"
    />
  </div>
</template>

<script setup lang="ts">
import type { Table, TableMeta } from "@tanstack/vue-table";
import { computed } from "vue";

import type { GenericModel } from "@/types/model";
import {
  type Selection,
  isRowSelected,
  toggleRowSelection,
} from "@/types/selection";

const props = defineProps<{
  table: Table<GenericModel>;
  item: GenericModel;
  selection: NonNullable<TableMeta<GenericModel>["selection"]>;
  label: () => string;
}>();

// `selection` prop shouldn't change
const { state, canSelect, lastSelectedId, shiftKeyHeld, hoveringId } =
  props.selection;

const isSelected = computed((): boolean => {
  return isRowSelected(state.value, props.item);
});

const isSelectable = computed((): boolean => {
  return canSelect?.(props.item, state.value) ?? true;
});

const label = computed((): string | undefined => {
  return `Select "${props.label()}"`;
});

const onInputChange = (ev: Event) => {
  if (!isSelectable.value && !isSelected.value) {
    return;
  }

  const input = ev.currentTarget as HTMLInputElement;
  const next = input.checked;

  const that = props.item;
  const thatId = that.id;

  // bulk selection
  if (shiftKeyHeld.value && lastSelectedId.value !== undefined) {
    const items = props.table.options.data;
    const lastId = lastSelectedId.value;

    // get index between lastSelectedId and thatId
    let indexA = -1;
    let indexB = -1;

    for (let idx = 0, len = items.length; idx < len; idx++) {
      const item = items[idx];

      if (item.id === thatId) {
        indexA = idx;
      }
      if (item.id === lastId) {
        indexB = idx;
      }

      if (indexA !== -1 && indexB !== -1) {
        break;
      }
    }

    if (indexA === -1 || indexB === -1) {
      return;
    }

    const startIndex = Math.min(indexA, indexB);
    const endIndex = Math.max(indexA, indexB);

    let itemsInRange = items.slice(startIndex, endIndex + 1);
    if (itemsInRange.length < 1) {
      return;
    }

    // Append existing selection if able to
    {
      const current = state.value;
      if (current && current.mode === "some") {
        itemsInRange = [...current.items, ...itemsInRange];
      }
    }

    let nextSelection: Selection<GenericModel> | undefined;

    if (!canSelect) {
      nextSelection = { mode: "some", items: itemsInRange as [GenericModel] };
    } else {
      for (let idx = 0, len = itemsInRange.length; idx < len; idx++) {
        const item = itemsInRange[idx];

        if (canSelect(item, nextSelection)) {
          if (nextSelection === undefined) {
            nextSelection = { mode: "some", items: [item] };
          } else {
            nextSelection.items.push(item);
          }
        }
      }
    }

    state.value = nextSelection;
    lastSelectedId.value = thatId;

    // Vue doesn't freeze controlled states like React, so what'll happen is
    // if we shift-click to an already-selected checkbox, it'll untick, because
    // the browser's toggle operation sees that it's already ticked.
    input.checked = true;
    return;
  }

  lastSelectedId.value = thatId;
  toggleRowSelection(state, that, next);
};

const onMouseEnter = () => {
  hoveringId.value = props.item.id;
};
const onMouseLeave = () => {
  hoveringId.value = undefined;
};
</script>
